import React, { MutableRefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { EditorContent } from '@tiptap/react';

import type { CellData, CellEditingSession } from '../../../../common/table/TableTypes';

import { MilanoteEditorRef } from '../../../components/editor/milanoteEditor/MilanoteEditorTypes';
import useClientElementTiptapEditor from '../../../components/tiptapEditor/useClientElementTiptapEditor';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { TABLE_CELL_EDITOR_KEY } from '../../../../common/table/tableConstants';
import { MNHotInstance } from '../tableHotInstanceTypes';
import useTableCellTextAlignment from './useTableCellTextAlignment';
import useTableCellTiptapExtensions from './useTableCellTiptapExtensions';
import { getTableCellEditorId } from '../utils/tableEditorSessionUtils';
import { getTiptapCellEditorContent, getTiptapContentFromPlainText } from './tiptapCellEditorContentUtils';
import type { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import getTiptapNodeTextContent from '../../../../common/tiptap/utils/jsonContentUtils/getTiptapNodeTextContent';
import useUpdatedRef from '../../../utils/react/useUpdatedRef';

type Props = {
    element: ImMNElement;
    elementId: string;
    isEditable: boolean;
    cellData: CellData | undefined;
    cellEditingSession: CellEditingSession | null;
    currentlyEditingEditorId: string | null;
    hotTableInstanceRef: MutableRefObject<MNHotInstance<TiptapContent, TiptapContent> | null>;
    cellEditorRef: MutableRefObject<MilanoteEditorRef<TiptapContent> | null>;
    locale: string;
};

const TiptapCellEditorComponent: React.FC<Props> = ({
    elementId,
    isEditable,
    cellData,
    cellEditingSession,
    currentlyEditingEditorId,
    hotTableInstanceRef,
    locale,
    cellEditorRef,
}) => {
    const { textAlignment, recomputeEditingTextAlignment } = useTableCellTextAlignment({
        cellData,
        cellEditingSession,
        locale,
    });

    const tiptapEditorRef = useRef<HTMLDivElement | null>(null);
    const cellEditingSessionRef = useUpdatedRef(cellEditingSession);
    const recomputeEditingTextAlignmentRef = useUpdatedRef(recomputeEditingTextAlignment);

    const saveContent = useCallback(
        (content: TiptapContent) => {
            if (!cellEditingSessionRef.current) return;

            recomputeEditingTextAlignmentRef.current(getTiptapNodeTextContent(content));

            const activeEditor = hotTableInstanceRef.current?.getMNEditor();
            activeEditor?.onMilanoteCellEditorChange(content);

            // TODO-TABLES: Reinstate once the handlers can accommodate non-draft content
            // hotTableInstanceRef.current?.runHooks('onMilanoteCellEditorChange', content);
        },
        [hotTableInstanceRef],
    );

    const extensions = useTableCellTiptapExtensions({ isEditable, saveContent });
    const editorId = getTableCellEditorId(elementId);

    const tiptapContent = useMemo(
        () => getTiptapCellEditorContent({ cellData, cellEditingSession, locale }),
        [cellData, cellEditingSession, locale],
    );

    const { editor } = useClientElementTiptapEditor({
        persistedContent: tiptapContent,
        extensions,

        editorId,
        editorKey: TABLE_CELL_EDITOR_KEY,
        currentEditorId: currentlyEditingEditorId || undefined,

        isEditable,
        isEditing: !!cellEditingSession,
        isSingleSelected: true,
    });

    useLayoutEffect(() => {
        if (!cellEditingSession || !editor || !tiptapContent) return;

        editor.commands.syncFromExternalStore(tiptapContent);
        editor.commands.focus('end');
    }, [cellEditingSession, editor, tiptapContent]);

    useEffect(() => {
        // TODO-TABLES: This can be refactored to be less hacky after draft is removed
        const editorWrapper: MilanoteEditorRef<TiptapContent> = {
            getEditor: () => editor,
            getEditorState: () => (editor ? (editor.getJSON() as any) : null),
            onChange: (content) => editor?.commands.setContent(content as any),
            setPlainText: (text: string, inlineStyles?: string[]) =>
                editor?.commands.setContent(getTiptapContentFromPlainText(text, inlineStyles)),
        };
        cellEditorRef.current = editorWrapper;

        return () => {
            if (cellEditorRef.current === editorWrapper) cellEditorRef.current = null;
        };
    }, [cellEditorRef, editor]);

    useEffect(() => {
        // This lets handsontable know which element is the active editor
        tiptapEditorRef.current!.setAttribute('data-hot-input', 'true');
    }, []);

    // Note that this component is initialised once *per table*, and is reused as the edit focus moves around
    // (ie a useEffect with an empty dependency array will run once on table init, not on every edit start)
    return (
        <div className="handsontable-input TableTiptapCellEditor" style={{ textAlign: textAlignment || undefined }}>
            <EditorContent
                innerRef={tiptapEditorRef}
                className="TableTiptapCellRenderer-content"
                dir="auto"
                editor={editor}
            />
        </div>
    );
};

export default TiptapCellEditorComponent;
